import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Email, LinkOff, Refresh, Send } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { userHasPermission } from '../../utils/permissions';
import { messageFromApiBody, toUserErrorMessage } from '../../utils/apiError';
import ClientAutocomplete, { ClientAutocompleteOption } from '../../components/Common/ClientAutocomplete';
import CaseAutocomplete, { CaseAutocompleteOption } from '../../components/Common/CaseAutocomplete';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import { getAuthToken } from '../../lib/authToken';

const PRIVILEGE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'attorney_client', label: 'Attorney–client' },
  { value: 'work_product', label: 'Work product' },
  { value: 'common_interest', label: 'Common interest' },
  { value: 'other', label: 'Other privilege' },
] as const;

interface OutboundEmailRow {
  id: string;
  subject: string;
  bodyText: string;
  bodyHtml?: string | null;
  toAddresses: string[];
  ccAddresses?: string[];
  bccAddresses?: string[];
  status: 'sent' | 'failed';
  provider?: string;
  sentAt?: string | null;
  createdAt?: string;
  errorMessage?: string | null;
  privilegeType?: string | null;
  isPrivilegeWithheld?: boolean;
  sender?: { id: string; firstName?: string; lastName?: string; email?: string };
  case?: { id: string; caseNumber?: string; title?: string } | null;
  client?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    entityLegalName?: string;
  } | null;
}

interface MailboxStatus {
  connected: boolean;
  active: {
    id: string;
    provider: 'gmail' | 'microsoft';
    email: string;
    status: string;
    ownership?: 'personal' | 'firm';
    lastError?: string | null;
  } | null;
  personal?: MailboxStatus['active'][];
  firm?: MailboxStatus['active'][];
  canManageFirm?: boolean;
  providersConfigured: { gmail: boolean; microsoft: boolean };
}

interface InboxMessage {
  id: string;
  provider: string;
  subject: string;
  from: string;
  date: string;
  snippet: string;
  unread?: boolean;
}

interface FilingRuleRow {
  id: string;
  name: string;
  enabled: boolean;
  priority: number;
  matchFromContains?: string | null;
  matchToContains?: string | null;
  matchSubjectContains?: string | null;
  caseId: string;
  privilegeType?: string | null;
  case?: { id: string; caseNumber?: string; title?: string } | null;
}

const authHeaders = () => ({
  Authorization: `Bearer ${getAuthToken()}`,
  'Content-Type': 'application/json',
});

const formatAddresses = (addrs?: string[]) =>
  Array.isArray(addrs) && addrs.length ? addrs.join(', ') : '—';

const clientDisplay = (c?: OutboundEmailRow['client']) => {
  if (!c) return null;
  if (c.entityLegalName) return c.entityLegalName;
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || null;
};

const MailPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const canWrite = userHasPermission(user, 'emails:write');

  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedInboxId, setSelectedInboxId] = useState<string | null>(null);

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preferSmtp, setPreferSmtp] = useState(false);
  const [privilegeType, setPrivilegeType] = useState('');
  const [client, setClient] = useState<ClientAutocompleteOption | null>(null);
  const [caseOption, setCaseOption] = useState<CaseAutocompleteOption | null>(null);
  const [fileCase, setFileCase] = useState<CaseAutocompleteOption | null>(null);
  const [ruleName, setRuleName] = useState('');
  const [ruleFrom, setRuleFrom] = useState('');
  const [ruleSubject, setRuleSubject] = useState('');
  const [ruleCase, setRuleCase] = useState<CaseAutocompleteOption | null>(null);
  const [rulePrivilege, setRulePrivilege] = useState('');

  useEffect(() => {
    const mailbox = searchParams.get('mailbox');
    if (!mailbox) return;
    if (mailbox === 'connected') {
      toast.success(`Mailbox connected (${searchParams.get('provider') || 'ok'})`);
      queryClient.invalidateQueries({ queryKey: ['mailbox-status', user?.tenantId] });
    } else if (mailbox === 'error') {
      toast.error(searchParams.get('message') || 'Mailbox connection failed');
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, queryClient, user?.tenantId]);

  const mailboxQuery = useQuery({
    queryKey: ['mailbox-status', user?.tenantId],
    enabled: Boolean(user?.tenantId),
    queryFn: async () => {
      const res = await fetch('/api/mailbox/status', { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'Failed to load mailbox status'));
      return json.data as MailboxStatus;
    },
  });

  const listQuery = useQuery({
    queryKey: ['outbound-emails', user?.tenantId, search],
    enabled: Boolean(user?.tenantId),
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '50' });
      if (search.trim()) params.set('q', search.trim());
      const res = await fetch(`/api/emails?${params}`, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'Failed to load sent mail'));
      return (json.data?.emails || []) as OutboundEmailRow[];
    },
  });

  const inboxQuery = useQuery({
    queryKey: ['mailbox-inbox', user?.tenantId],
    enabled: Boolean(user?.tenantId) && Boolean(mailboxQuery.data?.connected) && tab === 1,
    queryFn: async () => {
      const res = await fetch('/api/mailbox/inbox?limit=25', { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'Failed to load inbox'));
      return json.data as { provider: string; email: string; messages: InboxMessage[] };
    },
  });

  const rulesQuery = useQuery({
    queryKey: ['email-filing-rules', user?.tenantId],
    enabled: Boolean(user?.tenantId) && tab === 2,
    queryFn: async () => {
      const res = await fetch('/api/emails/filing-rules', { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'Failed to load filing rules'));
      return (json.data?.rules || []) as FilingRuleRow[];
    },
  });

  const emails = listQuery.data || [];
  const selected = useMemo(
    () => emails.find((e) => e.id === selectedId) || emails[0] || null,
    [emails, selectedId],
  );
  const inboxMessages = inboxQuery.data?.messages || [];
  const selectedInbox = useMemo(
    () => inboxMessages.find((m) => m.id === selectedInboxId) || inboxMessages[0] || null,
    [inboxMessages, selectedInboxId],
  );

  useEffect(() => {
    if (!selectedId && emails[0]?.id) setSelectedId(emails[0].id);
  }, [emails, selectedId]);

  useEffect(() => {
    if (!selectedInboxId && inboxMessages[0]?.id) setSelectedInboxId(inboxMessages[0].id);
  }, [inboxMessages, selectedInboxId]);

  const connectMutation = useMutation({
    mutationFn: async ({
      provider,
      ownership,
    }: {
      provider: 'gmail' | 'microsoft';
      ownership?: 'personal' | 'firm';
    }) => {
      const q = ownership === 'firm' ? '?ownership=firm' : '';
      const res = await fetch(`/api/mailbox/connect/${provider}${q}`, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, json.message || 'Connect failed'));
      return json.data as { url: string };
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err: unknown) => {
      toast.error(toUserErrorMessage(err, 'Could not start mailbox connect'));
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async (id?: string) => {
      const q = id ? `?id=${encodeURIComponent(id)}` : '';
      const res = await fetch(`/api/mailbox/disconnect${q}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'Disconnect failed'));
      return json;
    },
    onSuccess: () => {
      toast.success('Mailbox disconnected');
      queryClient.invalidateQueries({ queryKey: ['mailbox-status', user?.tenantId] });
      queryClient.invalidateQueries({ queryKey: ['mailbox-inbox', user?.tenantId] });
    },
    onError: (err: unknown) => {
      toast.error(toUserErrorMessage(err, 'Disconnect failed'));
    },
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/emails/send', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          to,
          cc: cc || undefined,
          subject,
          body,
          caseId: caseOption?.id || undefined,
          clientId: client?.id || caseOption?.clientId || undefined,
          preferSmtp,
          privilegeType: privilegeType || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(messageFromApiBody(json, json.message || 'Send failed'));
      }
      return json.data as OutboundEmailRow;
    },
    onSuccess: (row) => {
      toast.success(`Email sent via ${row.provider || 'smtp'}`);
      setTo('');
      setCc('');
      setSubject('');
      setBody('');
      setPrivilegeType('');
      setSelectedId(row.id);
      setTab(0);
      queryClient.invalidateQueries({ queryKey: ['outbound-emails', user?.tenantId] });
    },
    onError: (err: unknown) => {
      toast.error(toUserErrorMessage(err, 'Failed to send email'));
      queryClient.invalidateQueries({ queryKey: ['outbound-emails', user?.tenantId] });
    },
  });

  const fileMutation = useMutation({
    mutationFn: async () => {
      if (!selectedInbox) throw new Error('Select an inbox message');
      const res = await fetch('/api/mailbox/inbox/file', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          messageId: selectedInbox.id,
          subject: selectedInbox.subject,
          from: selectedInbox.from,
          snippet: selectedInbox.snippet,
          provider: selectedInbox.provider,
          date: selectedInbox.date,
          caseId: fileCase?.id || undefined,
          applyRules: !fileCase?.id,
          privilegeType: privilegeType || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'File to matter failed'));
      return json.data;
    },
    onSuccess: () => {
      toast.success('Email filed to matter');
      setFileCase(null);
    },
    onError: (err: unknown) => toast.error(toUserErrorMessage(err, 'File to matter failed')),
  });

  const createRuleMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/emails/filing-rules', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          name: ruleName.trim(),
          matchFromContains: ruleFrom.trim() || undefined,
          matchSubjectContains: ruleSubject.trim() || undefined,
          caseId: ruleCase?.id,
          privilegeType: rulePrivilege || undefined,
          priority: 100,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'Create rule failed'));
      return json.data;
    },
    onSuccess: () => {
      toast.success('Filing rule created');
      setRuleName('');
      setRuleFrom('');
      setRuleSubject('');
      setRuleCase(null);
      setRulePrivilege('');
      queryClient.invalidateQueries({ queryKey: ['email-filing-rules', user?.tenantId] });
    },
    onError: (err: unknown) => toast.error(toUserErrorMessage(err, 'Create rule failed')),
  });

  const deleteRuleMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/emails/filing-rules/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(messageFromApiBody(json, 'Delete failed'));
    },
    onSuccess: () => {
      toast.success('Rule deleted');
      queryClient.invalidateQueries({ queryKey: ['email-filing-rules', user?.tenantId] });
    },
    onError: (err: unknown) => toast.error(toUserErrorMessage(err, 'Delete failed')),
  });

  const onClientChange = useCallback((next: ClientAutocompleteOption | null) => {
    setClient(next);
    if (next?.email && !to.trim()) setTo(String(next.email));
  }, [to]);

  const onCaseChange = useCallback((next: CaseAutocompleteOption | null) => {
    setCaseOption(next);
    if (next?.client && typeof next.client === 'object') {
      const c = next.client as ClientAutocompleteOption;
      if (c.id) {
        setClient({
          id: c.id,
          firstName: c.firstName,
          lastName: c.lastName,
          email: (c as { email?: string }).email,
        });
        if ((c as { email?: string }).email && !to.trim()) {
          setTo(String((c as { email?: string }).email));
        }
      }
    }
  }, [to]);

  const handleSend = () => {
    if (!to.trim() || !subject.trim() || !body.trim()) {
      toast.error('To, subject, and body are required');
      return;
    }
    if (!caseOption?.id && !client?.id) {
      toast.error('Link this email to a case or client');
      return;
    }
    sendMutation.mutate();
  };

  const mailbox = mailboxQuery.data;
  const providers = mailbox?.providersConfigured || { gmail: false, microsoft: false };
  const canManageFirm = Boolean(mailbox?.canManageFirm);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email color="primary" /> Mail
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Compose, inbox, matter filing rules, privilege tags, and shared firm mailboxes.
          </Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          variant="outlined"
          size="small"
          onClick={() => {
            listQuery.refetch();
            mailboxQuery.refetch();
            if (tab === 1) inboxQuery.refetch();
            if (tab === 2) rulesQuery.refetch();
          }}
          disabled={listQuery.isFetching || mailboxQuery.isFetching}
        >
          Refresh
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Connected mailboxes
        </Typography>
        {mailboxQuery.isLoading ? (
          <CircularProgress size={22} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {(mailbox?.personal || []).filter((c) => c && c.status === 'active').length > 0 ? (
                (mailbox?.personal || []).filter((c) => c && c.status === 'active').map((c) => (
                  <Chip
                    key={c!.id}
                    color="success"
                    label={`Personal ${c!.provider === 'gmail' ? 'Gmail' : 'M365'}: ${c!.email}`}
                    onDelete={canWrite ? () => disconnectMutation.mutate(c!.id) : undefined}
                    deleteIcon={<LinkOff />}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No personal mailbox — sends use firm SMTP (or a firm mailbox if connected).
                </Typography>
              )}
              {canWrite && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!providers.gmail || connectMutation.isPending}
                    onClick={() => connectMutation.mutate({ provider: 'gmail' })}
                  >
                    Connect Gmail
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!providers.microsoft || connectMutation.isPending}
                    onClick={() => connectMutation.mutate({ provider: 'microsoft' })}
                  >
                    Connect Microsoft 365
                  </Button>
                </>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {(mailbox?.firm || []).filter((c) => c && c.status === 'active').length > 0 ? (
                (mailbox?.firm || []).filter((c) => c && c.status === 'active').map((c) => (
                  <Chip
                    key={c!.id}
                    color="info"
                    label={`Firm ${c!.provider === 'gmail' ? 'Gmail' : 'M365'}: ${c!.email}`}
                    onDelete={canManageFirm ? () => disconnectMutation.mutate(c!.id) : undefined}
                    deleteIcon={<LinkOff />}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No shared firm mailbox.
                </Typography>
              )}
              {canWrite && canManageFirm && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!providers.gmail || connectMutation.isPending}
                    onClick={() => connectMutation.mutate({ provider: 'gmail', ownership: 'firm' })}
                  >
                    Connect firm Gmail
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!providers.microsoft || connectMutation.isPending}
                    onClick={() => connectMutation.mutate({ provider: 'microsoft', ownership: 'firm' })}
                  >
                    Connect firm M365
                  </Button>
                </>
              )}
            </Box>
            {!providers.gmail && !providers.microsoft && (
              <Alert severity="warning">
                Mailbox OAuth is not configured yet. Add real Client ID/Secret values to
                `backend/.env` (`GOOGLE_MAIL_*` and/or `MICROSOFT_*`), then restart the backend.
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      <Grid container spacing={2}>
        {canWrite && (
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Compose
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <CaseAutocomplete
                  value={caseOption}
                  onChange={onCaseChange}
                  label="Case (optional if client set)"
                  size="small"
                  fullWidth
                />
                <ClientAutocomplete
                  value={client}
                  onChange={onClientChange}
                  label="Client (optional if case set)"
                  size="small"
                  fullWidth
                />
                <TextField
                  label="To"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  size="small"
                  fullWidth
                  required
                  helperText="Comma-separated addresses allowed"
                />
                <TextField
                  label="Cc"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  size="small"
                  fullWidth
                  required
                />
                <TextField
                  label="Message"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  multiline
                  minRows={8}
                  fullWidth
                  required
                />
                <FormControl size="small" fullWidth>
                  <InputLabel id="compose-privilege-label">Privilege tag</InputLabel>
                  <Select
                    labelId="compose-privilege-label"
                    label="Privilege tag"
                    value={privilegeType}
                    onChange={(e) => setPrivilegeType(String(e.target.value))}
                  >
                    {PRIVILEGE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value || 'none'} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {mailbox?.connected && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferSmtp}
                        onChange={(e) => setPreferSmtp(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Force firm SMTP (skip connected mailbox)"
                  />
                )}
                <Button
                  variant="contained"
                  startIcon={sendMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <Send />}
                  onClick={handleSend}
                  disabled={sendMutation.isPending}
                >
                  Send{mailbox?.connected && !preferSmtp ? ` via ${mailbox.active?.provider}` : ' via SMTP'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={canWrite ? 7 : 12}>
          <Paper variant="outlined" sx={{ p: 2, minHeight: 420 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1.5 }}>
              <Tab label="Sent" />
              <Tab label="Inbox" disabled={!mailbox?.connected} />
              <Tab label="Filing rules" />
            </Tabs>

            {tab === 0 && (
              <>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search subject or body…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Box>

                {listQuery.isError && (
                  <ListLoadErrorAlert
                    resourceLabel="sent mail"
                    message={toUserErrorMessage(listQuery.error, 'Failed to load sent mail')}
                    onRetry={() => listQuery.refetch()}
                  />
                )}

                {listQuery.isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={5}>
                      <List dense sx={{ maxHeight: 480, overflow: 'auto' }}>
                        {emails.length === 0 ? (
                          <Typography color="text.secondary" sx={{ p: 2 }}>
                            No sent emails yet.
                          </Typography>
                        ) : (
                          emails.map((row) => (
                            <ListItemButton
                              key={row.id}
                              selected={selected?.id === row.id}
                              onClick={() => setSelectedId(row.id)}
                            >
                              <ListItemText
                                primary={row.subject}
                                secondary={`${formatAddresses(row.toAddresses)} · ${
                                  row.sentAt || row.createdAt
                                    ? format(parseISO(String(row.sentAt || row.createdAt)), 'MMM d, yyyy h:mm a')
                                    : '—'
                                }`}
                                primaryTypographyProps={{ noWrap: true }}
                                secondaryTypographyProps={{ noWrap: true }}
                              />
                              <Chip
                                size="small"
                                label={row.provider || row.status}
                                color={row.status === 'sent' ? 'success' : 'error'}
                                sx={{ ml: 1 }}
                              />
                            </ListItemButton>
                          ))
                        )}
                      </List>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      {selected ? (
                        <Box>
                          <Typography variant="h6">{selected.subject}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            To: {formatAddresses(selected.toAddresses)}
                            {selected.ccAddresses?.length ? ` · Cc: ${formatAddresses(selected.ccAddresses)}` : ''}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <Chip size="small" label={selected.status} color={selected.status === 'sent' ? 'success' : 'error'} />
                            {selected.provider && <Chip size="small" variant="outlined" label={selected.provider} />}
                            {selected.privilegeType && (
                              <Chip size="small" color="warning" label={selected.privilegeType.replace(/_/g, ' ')} />
                            )}
                            {selected.case && (
                              <Chip size="small" label={`${selected.case.caseNumber || ''} ${selected.case.title || ''}`.trim()} />
                            )}
                            {clientDisplay(selected.client) && (
                              <Chip size="small" variant="outlined" label={clientDisplay(selected.client) || ''} />
                            )}
                          </Box>
                          {selected.errorMessage && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                              {selected.errorMessage}
                            </Alert>
                          )}
                          <Divider sx={{ my: 1 }} />
                          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0 }}>
                            {selected.bodyText}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography color="text.secondary">Select a message to preview.</Typography>
                      )}
                    </Grid>
                  </Grid>
                )}
              </>
            )}

            {tab === 1 && (
              <>
                {inboxQuery.isError && (
                  <ListLoadErrorAlert
                    resourceLabel="inbox"
                    message={toUserErrorMessage(inboxQuery.error, 'Failed to load inbox')}
                    onRetry={() => inboxQuery.refetch()}
                  />
                )}
                {inboxQuery.isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={5}>
                      <List dense sx={{ maxHeight: 480, overflow: 'auto' }}>
                        {inboxMessages.length === 0 ? (
                          <Typography color="text.secondary" sx={{ p: 2 }}>
                            Inbox is empty.
                          </Typography>
                        ) : (
                          inboxMessages.map((row) => (
                            <ListItemButton
                              key={row.id}
                              selected={selectedInbox?.id === row.id}
                              onClick={() => setSelectedInboxId(row.id)}
                            >
                              <ListItemText
                                primary={row.subject}
                                secondary={`${row.from} · ${row.date || '—'}`}
                                primaryTypographyProps={{
                                  noWrap: true,
                                  fontWeight: row.unread ? 700 : 400,
                                }}
                                secondaryTypographyProps={{ noWrap: true }}
                              />
                            </ListItemButton>
                          ))
                        )}
                      </List>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      {selectedInbox ? (
                        <Box>
                          <Typography variant="h6">{selectedInbox.subject}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            From: {selectedInbox.from}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="body2" sx={{ mb: 2 }}>{selectedInbox.snippet}</Typography>
                          {canWrite && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <CaseAutocomplete
                                value={fileCase}
                                onChange={setFileCase}
                                label="File to case (optional — leave blank to apply rules)"
                                size="small"
                                fullWidth
                              />
                              <Button
                                variant="contained"
                                size="small"
                                disabled={fileMutation.isPending}
                                onClick={() => fileMutation.mutate()}
                              >
                                {fileCase?.id ? 'File to selected matter' : 'File via matching rule'}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Typography color="text.secondary">Select an inbox message.</Typography>
                      )}
                    </Grid>
                  </Grid>
                )}
              </>
            )}

            {tab === 2 && (
              <>
                {rulesQuery.isError && (
                  <ListLoadErrorAlert
                    resourceLabel="filing rules"
                    message={toUserErrorMessage(rulesQuery.error, 'Failed to load filing rules')}
                    onRetry={() => rulesQuery.refetch()}
                  />
                )}
                {canWrite && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      New filing rule
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <TextField
                        size="small"
                        label="Rule name"
                        value={ruleName}
                        onChange={(e) => setRuleName(e.target.value)}
                        fullWidth
                      />
                      <TextField
                        size="small"
                        label="From contains"
                        value={ruleFrom}
                        onChange={(e) => setRuleFrom(e.target.value)}
                        fullWidth
                        placeholder="@client.com"
                      />
                      <TextField
                        size="small"
                        label="Subject contains"
                        value={ruleSubject}
                        onChange={(e) => setRuleSubject(e.target.value)}
                        fullWidth
                      />
                      <CaseAutocomplete
                        value={ruleCase}
                        onChange={setRuleCase}
                        label="File to case"
                        size="small"
                        fullWidth
                      />
                      <FormControl size="small" fullWidth>
                        <InputLabel id="rule-privilege-label">Privilege (optional)</InputLabel>
                        <Select
                          labelId="rule-privilege-label"
                          label="Privilege (optional)"
                          value={rulePrivilege}
                          onChange={(e) => setRulePrivilege(String(e.target.value))}
                        >
                          {PRIVILEGE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value || 'none'} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={
                          !ruleName.trim()
                          || !ruleCase?.id
                          || (!ruleFrom.trim() && !ruleSubject.trim())
                          || createRuleMutation.isPending
                        }
                        onClick={() => createRuleMutation.mutate()}
                      >
                        Create rule
                      </Button>
                    </Box>
                  </Paper>
                )}
                {rulesQuery.isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <List dense>
                    {(rulesQuery.data || []).length === 0 ? (
                      <Typography color="text.secondary" sx={{ p: 2 }}>
                        No filing rules yet. Match From/Subject → auto-file inbox messages to a matter.
                      </Typography>
                    ) : (
                      (rulesQuery.data || []).map((rule) => (
                        <ListItemButton key={rule.id} disableRipple>
                          <ListItemText
                            primary={`${rule.name} (priority ${rule.priority})`}
                            secondary={[
                              rule.enabled ? 'Enabled' : 'Disabled',
                              rule.matchFromContains ? `from:${rule.matchFromContains}` : null,
                              rule.matchSubjectContains ? `subject:${rule.matchSubjectContains}` : null,
                              rule.case
                                ? `→ ${rule.case.caseNumber || ''} ${rule.case.title || ''}`.trim()
                                : null,
                              rule.privilegeType || null,
                            ].filter(Boolean).join(' · ')}
                          />
                          {canWrite && (
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => deleteRuleMutation.mutate(rule.id)}
                            >
                              Delete
                            </Button>
                          )}
                        </ListItemButton>
                      ))
                    )}
                  </List>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MailPage;
